import { useState } from 'react'
import { ChevronDown, ChevronUp, Info } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { IdeologyDisplay, IdeologyMini } from '@/components/ideology/IdeologyDisplay'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { IdeologyVector } from '@/types'

interface PartyIdeologySectionProps {
  ideology: IdeologyVector
  className?: string
}

export function PartyIdeologySection({
  ideology,
  className,
}: PartyIdeologySectionProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">Ideology</CardTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <p>
                  Your party's ideology shifts through legislation and political
                  actions. It cannot be directly edited.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {isExpanded ? (
          <>
            <IdeologyDisplay ideology={ideology} showLabels compact={false} />
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => setIsExpanded(false)}
            >
              <ChevronUp className="h-4 w-4 mr-2" />
              Show Less
            </Button>
          </>
        ) : (
          <>
            {/* Compact view: mini dots + compact bars */}
            <div className="space-y-2">
              <IdeologyMini ideology={ideology} />
              <IdeologyDisplay ideology={ideology} showLabels={false} compact />
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => setIsExpanded(true)}
            >
              <ChevronDown className="h-4 w-4 mr-2" />
              View Full
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}
